import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { EChartsOption } from 'echarts';
import { AuthService } from 'src/app/core/services/auth.service';
import { StatistiqueService } from 'src/app/core/services/statistique.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardComponent implements OnInit {

  textHeading = 'Accueil';
  textBody = 'Tableau de Bord';

  statistiques: any = {
    soldeOuverture: 0,
    transactionsDuJour: 0,
    nombreCommandesJour: 0,
    nombreCommandesEnAttenteDeLivreur: 0,
    nombreCommandesEnAttente: 0,
    revenuNet: 0
  };

  activitesRecentes: any[] = [];
  chargementStatistiques = true;
  chargementActivites = true;
  chargementVueGlobale = true;
  chargementPartenaires = true;
  chargementInterurbain = true;
  vueGlobale: any = null;
  partenairesTransport: any = null;
  dashboardInterurbain: any = null;

  evolution30j: any[] = [];
  chargementEvolution = true;

  private _evolutionCache: Partial<Record<string, number>> = {};

  constructor(
    private statistiqueService: StatistiqueService,
    private router: Router,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    const utilisateur = this.authService.obtenirUtilisateurCourant();
    if (utilisateur?.role === 'LIVREUR') {
      this.router.navigate(['/livreur/mes-commandes']);
      return;
    }
    if (utilisateur?.role === 'RESPONSABLE_ZONE' || utilisateur?.role === 'GESTIONNAIRE_ZONE') {
      this.router.navigate(['/zone-manager/dashboard']);
      return;
    }
    if (utilisateur?.role === 'COMPTABLE_ZONE' || utilisateur?.role === 'COMPTABLE_GENERAL') {
      this.router.navigate(['/comptabilite/dashboard']);
      return;
    }
    this.chargerStatistiques();
    this.chargerActivitesRecentes();
    this.chargerVueGlobaleAdmin();
    this.chargerPartenairesTransport();
    this.chargerDashboardInterurbain();
    this.chargerEvolution();
  }

  chargerEvolution(): void {
    this.statistiqueService.obtenirEvolution(30).subscribe({
      next: (data) => {
        this.evolution30j = data || [];
        this._evolutionCache = {};
        this.chargementEvolution = false;
        this.cdr.markForCheck();
      },
      error: () => { this.chargementEvolution = false; this.cdr.markForCheck(); }
    });
  }

  /** Total cumulé mémoïsé — recalculé uniquement quand evolution30j change. */
  totalEvolution(serie: 'nbCommandes' | 'nbLivraisons' | 'revenu'): number {
    if (this._evolutionCache[serie] !== undefined) return this._evolutionCache[serie]!;
    const total = (this.evolution30j || []).reduce((s, d) => s + Number(d?.[serie] ?? 0), 0);
    this._evolutionCache[serie] = total;
    return total;
  }

  /** Options sparkline pour echarts (variante : 'nbCommandes' | 'nbLivraisons' | 'revenu'). */
  optionsSparkline(serie: 'nbCommandes' | 'nbLivraisons' | 'revenu', couleur: string): EChartsOption {
    const data = (this.evolution30j || []).map(d => Number(d[serie] ?? 0));
    const evolution = this.evolution30j;
    return {
      grid: { top: 4, right: 4, bottom: 4, left: 4 },
      xAxis: { type: 'category', show: false, boundaryGap: false, data: data.map((_, i) => String(i)) },
      yAxis: { show: false, type: 'value' },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const arr = Array.isArray(params) ? params : [params];
          const i = arr[0]?.dataIndex ?? 0;
          const d = evolution[i];
          if (!d) return '';
          const dateStr = d.date ? new Date(d.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '';
          return `${dateStr}<br/><strong>${arr[0].value}</strong>`;
        }
      },
      series: [{
        type: 'line', data, smooth: true, symbol: 'none',
        lineStyle: { width: 2, color: couleur },
        areaStyle: { color: couleur, opacity: 0.18 }
      }]
    };
  }

  chargerStatistiques(): void {
    this.statistiqueService.obtenirStatistiquesDashboard().subscribe({
      next: (donnees) => {
        this.statistiques = donnees;
        this.chargementStatistiques = false;
        this.cdr.markForCheck();
      },
      error: (erreur) => {
        console.error('Erreur lors du chargement des statistiques', erreur);
        this.chargementStatistiques = false;
        this.cdr.markForCheck();
      }
    });
  }

  chargerActivitesRecentes(): void {
    this.statistiqueService.obtenirActivitesRecentes().subscribe({
      next: (donnees) => {
        this.activitesRecentes = donnees;
        this.chargementActivites = false;
        this.cdr.markForCheck();
      },
      error: (erreur) => {
        console.error('Erreur lors du chargement des activités', erreur);
        this.chargementActivites = false;
        this.cdr.markForCheck();
      }
    });
  }

  chargerVueGlobaleAdmin(): void {
    this.statistiqueService.obtenirDashboardGlobalAdmin().subscribe({
      next: (donnees) => {
        this.vueGlobale = donnees;
        this.chargementVueGlobale = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.chargementVueGlobale = false;
        this.cdr.markForCheck();
      }
    });
  }

  chargerPartenairesTransport(): void {
    this.statistiqueService.obtenirTableauBordPartenairesTransport().subscribe({
      next: (donnees) => {
        this.partenairesTransport = donnees;
        this.chargementPartenaires = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.chargementPartenaires = false;
        this.cdr.markForCheck();
      }
    });
  }

  chargerDashboardInterurbain(): void {
    this.statistiqueService.obtenirDashboardInterurbain().subscribe({
      next: (donnees) => {
        this.dashboardInterurbain = donnees;
        this.chargementInterurbain = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.chargementInterurbain = false;
        this.cdr.markForCheck();
      }
    });
  }

  formaterMontant(montant: number): string {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montant);
  }

  obtenirClasseBadge(statut: string): string {
    const correspondanceStatut: { [cle: string]: string } = {
      'EN_ATTENTE': 'badge-warning',
      'EN_PREPARATION': 'badge-info',
      'AFFECTE': 'badge-primary',
      'EN_LIVRAISON': 'badge-info',
      'LIVRE': 'badge-success',
      'ANNULE': 'badge-error',
      'EN_RETARD': 'badge-error',
      'INCIDENT': 'badge-error'
    };
    return correspondanceStatut[statut] || 'badge-primary';
  }

  formaterDate(date: string): string {
    if (!date) return 'N/A';
    const d = new Date(date);
    const jour = String(d.getDate()).padStart(2, '0');
    const mois = String(d.getMonth() + 1).padStart(2, '0');
    const annee = d.getFullYear();
    const heures = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${jour}/${mois}/${annee} ${heures}:${minutes}`;
  }

  obtenirEntriesObjet(obj: any): { key: string, value: any }[] {
    if (!obj || typeof obj !== 'object') return [];
    return Object.entries(obj).map(([key, value]) => ({ key, value }));
  }

  // ─── Interurbain helpers ───────────────────────────────────────────────────

  libelleStatutInterurbain(code: string): string {
    const map: { [k: string]: string } = {
      EN_ATTENTE: 'En attente', AFFECTE: 'Livreur assigné',
      EN_PREPARATION: 'En route', ENLEVEMENT_EN_COURS: 'Enlèvement',
      RECUPERE: 'Récupéré', AU_DEPOT: 'Au dépôt', EN_TRI: 'Tri',
      EN_TRANSIT: 'En transit', RECEPTIONNE: 'Réceptionné',
      EN_DISTRIBUTION: 'Distribution', EN_LIVRAISON: 'En livraison',
      LIVRE: 'Livré', ANNULE: 'Annulé', INCIDENT: 'Incident', EN_RETARD: 'En retard'
    };
    return map[code] || code;
  }

  couleurStatutInterurbain(code: string): string {
    if (code === 'LIVRE') return '#059669';
    if (code === 'INCIDENT' || code === 'ANNULE') return '#DC2626';
    if (code === 'EN_TRANSIT') return '#7C3AED';
    if (code === 'EN_LIVRAISON' || code === 'EN_DISTRIBUTION' || code === 'RECEPTIONNE') return '#2563EB';
    if (code === 'AU_DEPOT' || code === 'EN_TRI' || code === 'RECUPERE') return '#EA580C';
    return '#64748B';
  }

  libelleModeEncaissement(mode: string): string {
    const map: { [k: string]: string } = {
      CASH: 'Espèces', MOBILE_MONEY: 'Mobile Money', VIREMENT: 'Virement', AUTRE: 'Autre'
    };
    return map[mode] || mode;
  }

  obtenirEntriesEncaissements(): { mode: string, montant: number, nombre: number }[] {
    const parMode = this.dashboardInterurbain?.encaissements?.parMode;
    if (!parMode) return [];
    return Object.entries(parMode).map(([mode, val]: [string, any]) => ({
      mode,
      montant: val.montant || 0,
      nombre: val.nombre || 0
    }));
  }

  formaterDelai(heures: number | null): string {
    if (heures == null) return 'N/A';
    if (heures < 24) return `${heures.toFixed(1)}h`;
    const jours = Math.floor(heures / 24);
    const h = Math.round(heures % 24);
    return h > 0 ? `${jours}j ${h}h` : `${jours}j`;
  }

  get greeting(): string {
    const h = new Date().getHours();
    if (h < 12) return 'Bonjour';
    if (h < 18) return 'Bon après-midi';
    return 'Bonsoir';
  }

  get prenomUtilisateur(): string {
    const u = this.authService.obtenirUtilisateurCourant();
    return (u as any)?.prenom || (u as any)?.nom?.split(' ')[0] || 'Admin';
  }

  get dateAujourdhui(): string {
    return new Date().toLocaleDateString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
  }

  couleurStatutCommande(statut: string): string {
    const map: { [k: string]: string } = {
      EN_ATTENTE: '#F59E0B', EN_PREPARATION: '#3B82F6', AFFECTE: '#6366F1',
      ENLEVEMENT_EN_COURS: '#F97316', RECUPERE: '#F97316', AU_DEPOT: '#EA580C',
      EN_TRI: '#8B5CF6', EN_TRANSIT: '#7C3AED', RECEPTIONNE: '#0EA5E9',
      EN_DISTRIBUTION: '#06B6D4', EN_LIVRAISON: '#0D9488', LIVRE: '#059669',
      ANNULE: '#DC2626', INCIDENT: '#991B1B', EN_RETARD: '#EF4444'
    };
    return map[statut] || '#94A3B8';
  }

  maxValeurStatut(): number {
    const obj = this.vueGlobale?.commandesParStatut;
    if (!obj) return 1;
    return Math.max(1, ...Object.values(obj).map(v => Number(v)));
  }

  totalCommandesStatut(): number {
    const obj = this.vueGlobale?.commandesParStatut;
    if (!obj) return 1;
    return Math.max(1, Object.values(obj).reduce((s: number, v) => s + Number(v), 0));
  }

  libelleStatutCommande(code: string): string {
    const map: { [k: string]: string } = {
      EN_ATTENTE: 'En attente', EN_PREPARATION: 'En préparation', AFFECTE: 'Livreur assigné',
      ENLEVEMENT_EN_COURS: 'Enlèvement', RECUPERE: 'Récupéré', AU_DEPOT: 'Au dépôt',
      EN_TRI: 'En tri', EN_TRANSIT: 'En transit', RECEPTIONNE: 'Réceptionné',
      EN_DISTRIBUTION: 'Distribution', EN_LIVRAISON: 'En livraison',
      LIVRE: 'Livré', ANNULE: 'Annulé', INCIDENT: 'Incident', EN_RETARD: 'En retard'
    };
    return map[code] || code;
  }
}
